import React from 'react';
import { useGetIdentity, useDataProvider } from '@refinedev/core';
import {
    Card,
    Form,
    Input,
    Button,
    Typography,
    Space,
    message,
    Row,
    Col
} from 'antd';
import { UserOutlined, MailOutlined, SaveOutlined } from '@ant-design/icons';

const { Title, Text } = Typography;

interface UserProfile {
    first_name: string;
    last_name: string;
    email: string;
    username: string;
}

export const UserSettings: React.FC = () => {
    const { data: user } = useGetIdentity();
    const dataProvider = useDataProvider()();
    const [form] = Form.useForm<UserProfile>();
    const [loading, setLoading] = React.useState(false);

    React.useEffect(() => {
        if (user) {
            // Fetch current user profile data
            fetchUserProfile();
        }
    }, [user]);

    const fetchUserProfile = async () => {
        try {
            // Use dataProvider's custom method (includes CSRF, 401/403 handling)
            const { data: profileData } = (await dataProvider.custom?.({
                url: '/api/me/',
                method: 'get',
            })) || {};

            if (profileData) {
                form.setFieldsValue(profileData as UserProfile);
            }
        } catch (error) {
            message.error('Failed to load profile data');
        }
    };

    const onFinish = async (values: UserProfile) => {
        setLoading(true);

        try {
            // Use dataProvider's custom method (includes CSRF, 401/403 handling)
            await dataProvider.custom?.({
                url: '/api/me/',
                method: 'patch',
                payload: values,
            });

            message.success('Profile updated successfully');
            // Refresh user data in the identity context
            window.location.reload(); // Simple approach to refresh user data
        } catch (error) {
            message.error('Failed to update profile');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div style={{ padding: '24px' }}>
            <Row justify="center">
                <Col xs={24} sm={20} md={16} lg={12} xl={10}>
                    <Card>
                        <Space direction="vertical" size="large" style={{ width: '100%' }}>
                            <div style={{ textAlign: 'center' }}>
                                <Title level={2}>
                                    <UserOutlined style={{ marginRight: '8px' }} />
                                    Profile Settings
                                </Title>
                                <Text type="secondary">
                                    Update your personal information
                                </Text>
                            </div>

                            <Form
                                form={form}
                                layout="vertical"
                                onFinish={onFinish}
                                size="large"
                            >
                                <Form.Item
                                    name="username"
                                    label="Username"
                                >
                                    <Input 
                                        prefix={<UserOutlined />}
                                        disabled
                                        placeholder="Username (cannot be changed)"
                                    />
                                </Form.Item>

                                <Form.Item
                                    name="first_name"
                                    label="First Name"
                                    rules={[
                                        { required: true, message: 'Please enter your first name' },
                                        { max: 150, message: 'First name must be less than 150 characters' }
                                    ]}
                                >
                                    <Input 
                                        placeholder="Enter your first name"
                                    />
                                </Form.Item>

                                <Form.Item
                                    name="last_name"
                                    label="Last Name"
                                    rules={[
                                        { required: true, message: 'Please enter your last name' },
                                        { max: 150, message: 'Last name must be less than 150 characters' }
                                    ]}
                                >
                                    <Input 
                                        placeholder="Enter your last name"
                                    />
                                </Form.Item>

                                <Form.Item
                                    name="email"
                                    label="Email Address"
                                    rules={[
                                        { required: true, message: 'Please enter your email address' },
                                        { type: 'email', message: 'Please enter a valid email address' }
                                    ]}
                                >
                                    <Input 
                                        prefix={<MailOutlined />}
                                        placeholder="Enter your email address"
                                    />
                                </Form.Item>

                                <Form.Item style={{ marginTop: '32px' }}>
                                    <Button
                                        type="primary"
                                        htmlType="submit"
                                        loading={loading}
                                        icon={<SaveOutlined />}
                                        block
                                        size="large"
                                    >
                                        Save Changes
                                    </Button>
                                </Form.Item>
                            </Form>
                        </Space>
                    </Card>
                </Col>
            </Row>
        </div>
    );
};